class ImageGalleryLightbox extends HTMLElement {
  constructor() {
    super();
    // Elements are now directly inside this custom element
    this.lightbox = this.querySelector('.gallery-lightbox');
    this.triggers = this.querySelectorAll('.gallery-lightbox-trigger');
    
    // Check if lightbox and triggers exist before proceeding
    if (!this.lightbox || this.triggers.length === 0) {
      console.warn('ImageGalleryLightbox: Lightbox or triggers not found.');
      return; 
    }

    this.closeButton = this.lightbox.querySelector('.gallery-lightbox__close');
    this.prevButton = this.lightbox.querySelector('.gallery-lightbox__prev');
    this.nextButton = this.lightbox.querySelector('.gallery-lightbox__next');
    this.lightboxImage = this.lightbox.querySelector('.gallery-lightbox__image');
    this.lightboxOverlay = this.lightbox.querySelector('.gallery-lightbox__overlay');

    // Add checks for buttons too
    if (!this.closeButton || !this.prevButton || !this.nextButton || !this.lightboxImage || !this.lightboxOverlay) {
      console.error('ImageGalleryLightbox: One or more lightbox components are missing.');
      return;
    }

    this.images = Array.from(this.triggers).map(trigger => ({
      src: trigger.href,
      alt: trigger.querySelector('img')?.alt || '',
    }));

    this.currentIndex = 0;
    this.previouslyFocusedElement = null; // Initialize here

    this.init();
  }

  connectedCallback() {
    // init() is often called in connectedCallback for custom elements
    // to ensure the element is in the DOM, but constructor might be okay here.
    // If issues persist, move the this.init() call here.
     if (!this.initialized) { // Prevent multiple initializations
       this.init();
       this.initialized = true;
     }
  }

  init() {
    // Guard against initializing if elements weren't found in constructor
    if (!this.lightbox || !this.triggers || !this.closeButton) return;

    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', this.openLightbox.bind(this));
    });

    this.closeButton.addEventListener('click', this.close.bind(this));
    this.lightboxOverlay.addEventListener('click', this.close.bind(this));
    this.prevButton.addEventListener('click', this.showPrevImage.bind(this));
    this.nextButton.addEventListener('click', this.showNextImage.bind(this));

    // Keyboard navigation
    this.lightbox.addEventListener('keydown', (event) => {
      if (!this.lightbox.hasAttribute('open')) return; // Only handle keys if open

      switch (event.key) {
        case 'Escape':
          this.close();
          break;
        case 'ArrowLeft':
          this.showPrevImage();
          break;
        case 'ArrowRight':
          this.showNextImage();
          break;
      }
    });
    console.log('ImageGalleryLightbox initialized successfully.'); // Add log
  }

  openLightbox(event) {
    event.preventDefault(); // Crucial: Stop the link navigation
    console.log('Lightbox trigger clicked'); // Add log
    
    const trigger = event.currentTarget;
    const index = parseInt(trigger.dataset.index, 10);
    
    if (isNaN(index)) {
       console.error('ImageGalleryLightbox: Invalid index on trigger.', trigger);
       return;
    }

    this.currentIndex = index;
    this.updateImage();
    
    this.previouslyFocusedElement = document.activeElement; // Store focus before opening

    this.lightbox.setAttribute('open', '');
    document.body.style.overflow = 'hidden'; // Prevent background scrolling

    // Focus management for accessibility - delay slightly if needed
    requestAnimationFrame(() => {
        this.closeButton.focus(); // Focus the close button initially
    });
  }

  close() {
    if (!this.lightbox.hasAttribute('open')) return; // Don't close if already closed
    console.log('Closing lightbox'); // Add log
    this.lightbox.removeAttribute('open');
    document.body.style.overflow = ''; // Restore scrolling
    this.lightboxImage.src = ''; // Clear image src
    this.lightboxImage.alt = '';

    // Restore focus
    if (this.previouslyFocusedElement) {
        this.previouslyFocusedElement.focus();
        this.previouslyFocusedElement = null; // Clear stored element
    }
  }

  updateImage() {
    if (this.images.length === 0) return;
    const image = this.images[this.currentIndex];
    this.lightboxImage.src = image.src;
    this.lightboxImage.alt = image.alt;
    
    // Show/hide nav buttons based on index
    this.prevButton.style.display = this.currentIndex === 0 ? 'none' : '';
    this.nextButton.style.display = this.currentIndex === this.images.length - 1 ? 'none' : '';
  }

  showPrevImage() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateImage();
    }
  }

  showNextImage() {
    if (this.currentIndex < this.images.length - 1) {
      this.currentIndex++;
      this.updateImage();
    }
  }
}

// Define the custom element if it doesn't exist
if (!customElements.get('image-gallery-lightbox')) {
  customElements.define('image-gallery-lightbox', ImageGalleryLightbox);
}

// REMOVED the manual instantiation loop 